import {
  FinancialApplicationFormSectionSummary,
  FinancialApplicationFormSectionSummaryDataRow,
} from "../FinancialApplicationFormSectionSummary/FinancialApplicationFormSectionSummary";
import { useFieldSchemaToGetDataForKeyPersonSummary } from "../FinancialApplicationFormSectionSummary/useFieldSchemaToGetDataForSummary";
import { FieldInput, getHiddenGovernmentIdFields } from "@/utils/getFieldSchemaData";
import { COMPLEX_FIELD_KEYS, FIELD_KEYS } from "@/constants/fieldKeys";
import { useKeyPeopleSummary } from "@/hooks/useKeyPeopleSummary";
import { useMemo } from "react";
import { SUBSTEP_PATHS } from "@/config/stepPaths";

export interface KeyPeopleSummaryV2Props {
  financialApplicationId: string;
}

export const KeyPeopleSummaryV2: React.FC<KeyPeopleSummaryV2Props> = ({
  financialApplicationId,
}) => {
  const { keyPeople, isLoading } = useKeyPeopleSummary(financialApplicationId);

  const summaryData = useMemo(() => {
    const data: FinancialApplicationFormSectionSummaryDataRow[] = [];

    // Process each key person
    keyPeople.forEach((person) => {
      // Use the reusable utility function to determine hidden government ID fields
      const hiddenGovernmentIdFields = getHiddenGovernmentIdFields(person.governmentIdInformation);
      
      const fieldsToInclude: FieldInput[] = [
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.BENEFICIAL_OWNER_PERCENTAGE,
        },
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION,
        },
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION,
          complexFieldValue: {
            hiddenFields: hiddenGovernmentIdFields,
          },
        },
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION,
        },
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION,
          complexFieldValue: {
            hiddenFields: [
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_USERNAME,
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_ROLE,
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_EMAIL_ADDRESS,
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_PHONE_NUMBER,
            ],
          },
        },
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION,
        },
        {
          fieldProps: FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION,
          complexFieldValue: {
            hiddenFields: [
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_NAME_DISPLAYED,
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.BUSINESS_NAME_DISPLAYED_ON_CARD,
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.USE_HOME_ADDRESS_FOR_DEBIT_CARD_MAILING_ADDRESS,
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_MAILING_ADDRESS,
            ],
          },
        },
      ];
      
      const personSummaryData = useFieldSchemaToGetDataForKeyPersonSummary(person, fieldsToInclude);
      data.push(...personSummaryData);
    });

    return data;
  }, [keyPeople]);

  if (isLoading) {
    return null; // Or a loading spinner if preferred
  }

  return (
    <FinancialApplicationFormSectionSummary
      title="Key People"
      data={summaryData}
      navigateTo={`/step/${SUBSTEP_PATHS.EntityInfo.KeyPeople}`}
    />
  );
};
